import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logActivity } from "@/lib/logActivity";
import { createNotification } from "@/lib/createNotification";
import UpgradeModal from "@/components/base/UpgradeModal";
import { checkLimit, bumpLimitCache, type LimitCheckResult } from "@/lib/planLimits";
import { queueEmail, contractCreatedBody } from "@/lib/queueEmail";
import ContractModal, { type ContractSavePayload } from "./components/ContractModal";
import { activateAddonFull } from "@/lib/contractAddons";
import FromTemplateModal from "./components/FromTemplateModal";
import { printContract } from "@/lib/printContract";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import StatusBadge from "@/components/base/StatusBadge";
import LoadingState from "@/components/base/LoadingState";
import { buildPaidByContract } from "@/lib/paymentStatusHelper";
import { applyPaymentStatusBatch } from "@/lib/contractBalance";

type Contract = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_number: string;
  title: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  status: string;
  signed_at: string | null;
  created_at: string;
  updated_at: string;
  client_name: string;
  template_id?: string | null;
  rendered_content?: string | null;
  signature_status?: string | null;
  client_signature?: string | null;
  payment_link?: string | null;
  payment_status?: string;
};

type TemplatePayload = {
  template_id: string;
  rendered_content: string;
  client_id: string;
  client_name: string;
  title: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  contract_number: string;
  status: string;
};

type ClientOption = { id: string; full_name: string; email: string };

export default function AdminContracts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingNew, setPendingNew] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [fromTemplateOpen, setFromTemplateOpen] = useState(false);
  const [savingFromTpl, setSavingFromTpl]       = useState(false);
  const [upgradeResult, setUpgradeResult]       = useState<LimitCheckResult | null>(null);
  const [tenantName, setTenantName]             = useState("");

  useEffect(() => {
    if (searchParams.get("new") === "1") setPendingNew(true);
  }, [searchParams]);

  useEffect(() => {
    if (!loading && pendingNew) { setPendingNew(false); handleNew(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pendingNew]);

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    const [contractsRes, clientsRes, tenantRes, paymentsRes] = await Promise.all([
      supabase.from("contracts").select("*").eq("tenant_id", tid).order("created_at", { ascending: false }),
      supabase.from("clients").select("id, full_name, email").eq("tenant_id", tid).eq("status", "active"),
      supabase.from("saas_tenants").select("name").eq("id", tid).maybeSingle(),
      supabase.from("payments").select("contract_id, amount, status").eq("tenant_id", tid),
    ]);
    if (!clientsRes.error && clientsRes.data) setClientOptions(clientsRes.data as ClientOption[]);
    if (!tenantRes.error && tenantRes.data) setTenantName((tenantRes.data as { name: string }).name ?? "");

    if (!contractsRes.error && contractsRes.data) {
      const paidByContract = buildPaidByContract(
        (paymentsRes.data ?? []) as { contract_id: string | null; amount: number; status: string }[],
      );
      const calculated = applyPaymentStatusBatch(
        contractsRes.data as Contract[],
        paidByContract,
      );
      setContracts(calculated);

      // Silently batch-update changed payment statuses
      const original = contractsRes.data as Contract[];
      const changed = calculated.filter((c, i) => c.payment_status !== original[i].payment_status);
      if (changed.length > 0) {
        await Promise.all(
          changed.map((c) =>
            supabase
              .from("contracts")
              .update({ payment_status: c.payment_status, updated_at: new Date().toISOString() })
              .eq("id", c.id),
          ),
        );
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  const filtered = contracts.filter((c) => {
    const matchSearch =
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.contract_number.toLowerCase().includes(search.toLowerCase()) ||
      c.client_name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalContracts = filtered.length;
  const totalValue = filtered.reduce((a, b) => a + Number(b.total_amount), 0);
  const totalBalance = filtered.reduce((a, b) => a + Number(b.balance_amount), 0);

  const handleEdit = (contract: Contract) => {
    setSelectedContract(contract);
    setIsNew(false);
    setModalOpen(true);
  };

  const handleNew = async () => {
    const limitResult = await checkLimit('contracts');
    if (limitResult && !limitResult.allowed) {
      setUpgradeResult(limitResult);
      return;
    }
    setSelectedContract(null);
    setIsNew(true);
    setModalOpen(true);
  };

  const handleSaveFromTemplate = async (payload: TemplatePayload) => {
    if (!tenantId) return;
    setSavingFromTpl(true);
    const { data: created, error } = await supabase
      .from("contracts")
      .insert({ ...payload, tenant_id: tenantId })
      .select()
      .maybeSingle();
    if (!error && created) {
      setContracts((prev) => [created as Contract, ...prev]);
      bumpLimitCache('contracts');
      logActivity({ tenantId, action: "created", entity: "contract", entityId: (created as Contract).id, entityName: (created as Contract).title });
      createNotification({ tenantId, type: "success", title: "Contract created from template", message: `Contract "${(created as Contract).contract_number}" for ${payload.client_name} has been created.` });
      const clientRecord = clientOptions.find((c) => c.id === payload.client_id);
      if (clientRecord?.email) {
        queueEmail({
          tenantId,
          toEmail: clientRecord.email,
          subject: `Contract ${(created as Contract).contract_number} - ${(created as Contract).title}`,
          body: contractCreatedBody({
            clientName:     clientRecord.full_name,
            contractNumber: (created as Contract).contract_number,
            contractTitle:  (created as Contract).title,
            eventDate:      (created as Contract).event_date,
            totalAmount:    Number((created as Contract).total_amount),
          }),
        });
      }
      setFromTemplateOpen(false);
    }
    setSavingFromTpl(false);
  };

  /* ── Unified addon activation via contract_addons table ── */
  const applyAddon = async (
    contractId: string,
    clientId: string,
    addonKey: string,
    mode: "none" | "paid" | "gift" | "promo",
    price: number,
    promoCode: string,
    alreadyActive: boolean,
  ) => {
    if (!tenantId || mode === "none" || alreadyActive) return;
    const clientRecord = clientOptions.find((c) => c.id === clientId);
    await activateAddonFull({
      tenantId,
      contractId,
      clientId,
      addonKey,
      mode: mode as "paid" | "gift" | "promo",
      price,
      promoCode,
      clientName: clientRecord?.full_name,
    });
  };

  const handleSave = async (payload: ContractSavePayload) => {
    if (!tenantId) return;
    const data = payload.contract;

    if (isNew) {
      const { id: _id, ...insertData } = data;
      const { data: created, error } = await supabase
        .from("contracts")
        .insert({ ...insertData, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (!error && created) {
        setContracts((prev) => [created as Contract, ...prev]);
        bumpLimitCache('contracts');
        logActivity({ tenantId, action: "created", entity: "contract", entityId: (created as Contract).id, entityName: (created as Contract).title });
        createNotification({ tenantId, type: 'success', title: 'New contract created', message: `Contract "${(created as Contract).contract_number}" for ${(created as Contract).client_name || data.client_name} has been created.` });
        const clientRecord = clientOptions.find((c) => c.id === (created as Contract).client_id);
        if (clientRecord?.email) {
          queueEmail({
            tenantId,
            toEmail: clientRecord.email,
            subject: `Contract ${(created as Contract).contract_number} - ${(created as Contract).title}`,
            body: contractCreatedBody({
              clientName:      clientRecord.full_name,
              contractNumber:  (created as Contract).contract_number,
              contractTitle:   (created as Contract).title,
              eventDate:       (created as Contract).event_date,
              totalAmount:     Number((created as Contract).total_amount),
            }),
          });
        }
        // Apply addon side-effects via unified pipeline
        await applyAddon(
          (created as Contract).id,
          (created as Contract).client_id,
          "digital_invitation",
          payload.invitationAddon.mode,
          payload.invitationAddon.amount,
          payload.invitationAddon.promoCode,
          payload.invitationAddon.alreadyActive,
        );
        await applyAddon(
          (created as Contract).id,
          (created as Contract).client_id,
          "signature_book",
          payload.signatureBookAddon.mode,
          payload.signatureBookAddon.amount,
          payload.signatureBookAddon.promoCode,
          payload.signatureBookAddon.alreadyActive,
        );
      }
    } else {
      const { data: updated, error } = await supabase
        .from("contracts")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (!error && updated) {
        setContracts((prev) => prev.map((c) => (c.id === (updated as Contract).id ? (updated as Contract) : c)));
        logActivity({ tenantId, action: "updated", entity: "contract", entityId: data.id, entityName: data.title });
        // Apply addon side-effects (edit too)
        await applyAddon(
          data.id,
          data.client_id,
          "digital_invitation",
          payload.invitationAddon.mode,
          payload.invitationAddon.amount,
          payload.invitationAddon.promoCode,
          payload.invitationAddon.alreadyActive,
        );
        await applyAddon(
          data.id,
          data.client_id,
          "signature_book",
          payload.signatureBookAddon.mode,
          payload.signatureBookAddon.amount,
          payload.signatureBookAddon.promoCode,
          payload.signatureBookAddon.alreadyActive,
        );
      }
    }
    setModalOpen(false);
  };

  return (
    <AdminLayout>
      <AdminHeader
        title="Contracts"
        subtitle="Manage your studio contracts and agreements"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFromTemplateOpen(true)}
              className="flex items-center gap-2 bg-white/8 hover:bg-white/15 border border-slate-200 text-[#0f172a]/80 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-copy-2-line"></i>
              </span>
              From Template
            </button>
            <button
              onClick={handleNew}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              New Contract
            </button>
          </div>
        }
      />
      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border border-slate-200 rounded-xl p-4 border border-slate-200">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Contracts Shown</p>
            <p className="text-[#0f172a] text-2xl font-bold">{totalContracts}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 rounded-xl p-4 border border-slate-200">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Total Value</p>
            <p className="text-[#C8A96E] text-2xl font-bold">${totalValue.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 rounded-xl p-4 border border-slate-200">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Pending Balance</p>
            <p className="text-amber-400 text-2xl font-bold">${totalBalance.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contracts..."
              className="w-full bg-white rounded-lg border border-slate-200 pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/60 transition-colors"
            />
          </div>
          <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1">
            {["all", "draft", "active", "signed", "completed", "cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  statusFilter === s ? "bg-[#C8A96E] text-black" : "text-slate-400 hover:text-[#0f172a]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : (
            <div className="overflow-x-auto"><table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Contract</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Client</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Event</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Package</th>
                  <th className="text-right text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Total</th>
                  <th className="text-right text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Balance</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Status</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Payment</th>
                  <th className="px-5 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <p className="text-[#0f172a] text-sm font-medium">{c.contract_number}</p>
                      <p className="text-slate-400 text-xs truncate max-w-[160px]">{c.title}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-600 text-sm">{c.client_name}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-600 text-sm">{c.event_date}</p>
                      <p className="text-slate-400 text-xs truncate max-w-[140px]">{c.event_location}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-500 text-sm truncate max-w-[140px]">{c.package_name}</p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <p className="text-[#0f172a] font-semibold text-sm">${Number(c.total_amount).toLocaleString()}</p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <p className={`font-semibold text-sm ${Number(c.balance_amount) > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                        ${Number(c.balance_amount).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-3.5">
                      <PaymentStatusBadge status={c.payment_status} />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/admin/contracts/${c.id}`)}
                          title="View Detail"
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        <button
                          onClick={() =>
                            printContract(
                              {
                                contract_number:  c.contract_number,
                                title:            c.title,
                                status:           c.status,
                                event_date:       c.event_date,
                                event_location:   c.event_location,
                                package_name:     c.package_name,
                                total_amount:     c.total_amount,
                                deposit_amount:   c.deposit_amount,
                                balance_amount:   c.balance_amount,
                                client_name:      c.client_name,
                                rendered_content: c.rendered_content,
                                signature_status: c.signature_status,
                                client_signature: c.client_signature,
                                signed_at:        c.signed_at,
                              },
                              tenantName,
                            )
                          }
                          title="Print / Save PDF"
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                        >
                          <i className="ri-printer-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleEdit(c)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="py-16">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
                          <i className="ri-file-text-line text-[#C8A96E] text-2xl"></i>
                        </div>
                        <div>
                          <p className="text-[#0f172a] font-medium text-sm mb-1">
                            {contracts.length === 0 ? "No contracts yet" : "No contracts found"}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {contracts.length === 0
                              ? "Create your first contract to track client agreements and payment schedules."
                              : "Try adjusting your search or status filter."}
                          </p>
                        </div>
                        {contracts.length === 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <button
                              onClick={() => setFromTemplateOpen(true)}
                              className="flex items-center gap-2 bg-white/8 hover:bg-white/15 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-file-copy-2-line"></i>
                              From Template
                            </button>
                            <button
                              onClick={handleNew}
                              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-add-line"></i>
                              New Contract
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {modalOpen && (
        <ContractModal
          contract={selectedContract}
          isNew={isNew}
          tenantId={tenantId ?? ""}
          clients={clientOptions}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {upgradeResult && (
        <UpgradeModal
          result={upgradeResult}
          onClose={() => setUpgradeResult(null)}
        />
      )}

      {fromTemplateOpen && (
        <FromTemplateModal
          tenantId={tenantId ?? ""}
          tenantName={tenantName}
          clients={clientOptions}
          saving={savingFromTpl}
          onClose={() => setFromTemplateOpen(false)}
          onSave={handleSaveFromTemplate}
        />
      )}
    </AdminLayout>
  );
}
